import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import DashboardLayout from '../../components/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Clock, BookOpen, GraduationCap, Activity, Plus, Edit, Trash2, Loader2, Info } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const SchoolCalendar = () => {
    const { user } = useAuth();
    const [currentDate, setCurrentDate] = useState(new Date());
    const [programs, setPrograms] = useState([]);
    const [homework, setHomework] = useState([]);
    const [activities, setActivities] = useState([]);
    const [adminEvents, setAdminEvents] = useState([]);
    const [closedDates, setClosedDates] = useState([]); // ["2026-08-15", …]
    const [programClosed, setProgramClosed] = useState({}); // { program_id: ["2026-10-01", …] }
    const [classesStartDate, setClassesStartDate] = useState('2026-09-19');
    const [teacherSessions, setTeacherSessions] = useState([]); // [{class_name,date,start_time,end_time,program_name},…]
    const [classes, setClasses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState(null);

    // Event editor (super_admin only)
    const emptyEvent = { title: '', description: '', start: '', end: '', all_day: false, color: '#064E3B', target_roles: ['parent','teacher'], branches: [], program_ids: [], class_ids: [] };
    const [eventDialog, setEventDialog] = useState(null); // event object OR null
    const [saving, setSaving] = useState(false);
    const isAdmin = user?.role === 'super_admin';

    const fetchData = useCallback(async () => {
        try {
            let programsData = [];

            if (user?.role === 'parent') {
                // Parents: only fetch programs their children are enrolled in
                const childrenRes = await axios.get(`${API_URL}/api/students`, { withCredentials: true });
                const childProgramIds = [...new Set(childrenRes.data.map(c => c.program_id).filter(Boolean))];
                if (childProgramIds.length > 0) {
                    const allPrograms = await axios.get(`${API_URL}/api/programs`, { withCredentials: true });
                    programsData = allPrograms.data.filter(p => childProgramIds.includes(p.id));
                }
            } else {
                const res = await axios.get(`${API_URL}/api/programs`, { withCredentials: true });
                programsData = res.data;
            }
            setPrograms(programsData);
            // Classes for admin event audience picker
            if (isAdmin) {
                try {
                    const cRes = await axios.get(`${API_URL}/api/classes`, { withCredentials: true });
                    setClasses(cRes.data || []);
                } catch { /* ignore */ }
            }

            // Admin-authored events + school-days overlay (holidays + teacher class sessions)
            const now = new Date();
            const rangeStart = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().slice(0,10);
            const rangeEnd = new Date(now.getFullYear(), now.getMonth() + 3, 0).toISOString().slice(0,10);
            const [evRes, sdRes] = await Promise.all([
                axios.get(`${API_URL}/api/calendar/events`, { withCredentials: true }).catch(() => ({ data: [] })),
                axios.get(`${API_URL}/api/calendar/school-days`, { params: { start: rangeStart, end: rangeEnd }, withCredentials: true }).catch(() => ({ data: {} })),
            ]);
            setAdminEvents(evRes.data || []);
            setClosedDates(sdRes.data?.closed_dates || []);
            setProgramClosed(sdRes.data?.program_closed_dates || {});
            setClassesStartDate(sdRes.data?.classes_start_date || '2026-09-19');
            setTeacherSessions(sdRes.data?.teacher_sessions || []);

            const extraRequests = [];
            if (['parent', 'teacher', 'super_admin'].includes(user?.role)) {
                extraRequests.push(axios.get(`${API_URL}/api/homework`, { withCredentials: true }).catch(() => ({ data: [] })));
            }
            extraRequests.push(axios.get(`${API_URL}/api/activities`, { withCredentials: true }).catch(() => ({ data: [] })));

            const extraResults = await Promise.all(extraRequests);
            if (extraResults[0]) setHomework(extraResults[0].data || []);
            if (extraResults.length > 1) setActivities(extraResults[1].data || []);
            else if (extraResults[0] && !['parent', 'teacher', 'super_admin'].includes(user?.role)) setActivities(extraResults[0].data || []);
        } catch (error) {
            console.error('Failed to fetch calendar data:', error);
        } finally {
            setLoading(false);
        }
    }, [user?.role, isAdmin]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    // Monday-first offset (Sun=0 → 6, Mon=1 → 0, Tue=2 → 1, …)
    const mondayOffset = (firstDay + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
    const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));

    const dayMap = { 'Sunday': 0, 'Monday': 1, 'Tuesday': 2, 'Wednesday': 3, 'Thursday': 4, 'Friday': 5, 'Saturday': 6 };

    const closedSet = useMemo(() => new Set(closedDates), [closedDates]);
    const isClosed = (dateStr) => closedSet.has(dateStr);

    const getEventsForDate = (day) => {
        const date = new Date(year, month, day);
        const dayOfWeek = date.getDay();
        const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        if (isClosed(dateStr)) return [];  // School closed → no events at all
        const events = [];

        // Teachers see their expanded class sessions (server-computed, holiday-aware)
        if (user?.role === 'teacher') {
            teacherSessions.filter(s => s.date === dateStr).forEach(s => {
                events.push({ type: 'class', label: `${s.class_name}${s.program_name ? ' — ' + s.program_name : ''}`, time: `${s.start_time}-${s.end_time}`, color: 'bg-[#064E3B]' });
            });
        } else {
            // Other roles: program-level class days — only from the term start date,
            // and skipping any program/branch-scoped holiday dates.
            if (dateStr >= classesStartDate) {
                programs.forEach(p => {
                    if (!p.days?.some(d => dayMap[d] === dayOfWeek)) return;
                    if ((programClosed[p.id] || []).includes(dateStr)) return;
                    events.push({ type: 'class', label: p.short_name || p.name, time: `${p.start_time}-${p.end_time}`, color: 'bg-[#064E3B]' });
                });
            }
        }

        // Homework due dates
        homework.forEach(hw => {
            if (hw.due_date === dateStr) {
                events.push({ type: 'homework', label: hw.title, color: 'bg-[#D97706]' });
            }
        });

        // Activities
        activities.forEach(act => {
            if (act.start_date <= dateStr && act.end_date >= dateStr) {
                events.push({ type: 'activity', label: act.name, color: 'bg-[#7C3AED]' });
            }
        });

        // Admin-authored events (already audience-filtered by backend)
        adminEvents.forEach(ev => {
            const evDate = (ev.start || '').slice(0, 10);
            const evEndDate = (ev.end || ev.start || '').slice(0, 10);
            if (evDate <= dateStr && evEndDate >= dateStr) {
                events.push({
                    type: 'admin',
                    id: ev.id,
                    label: ev.title,
                    time: ev.all_day ? '' : (ev.start || '').slice(11, 16),
                    color: 'bg-[#EF4444]',
                    _raw: ev,
                });
            }
        });

        return events;
    };

    const selectedDateStr = selectedDate ? `${year}-${String(month + 1).padStart(2, '0')}-${String(selectedDate).padStart(2, '0')}` : null;
    const selectedEvents = selectedDate ? getEventsForDate(selectedDate) : [];

    const cells = [];
    for (let i = 0; i < mondayOffset; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);

    const today = new Date();
    const isToday = (day) => day && today.getFullYear() === year && today.getMonth() === month && today.getDate() === day;

    if (loading) {
        return (
            <DashboardLayout>
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#064E3B]"></div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="calendar-page">
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                        <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit']">Calendar</h1>
                        <p className="text-[#4B5563]">School schedule and upcoming events</p>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={() => window.print()} data-testid="calendar-print-btn">
                            <CalendarIcon className="h-4 w-4 mr-1" />Print Annual (A4)
                        </Button>
                        {isAdmin && (
                            <Button className="bg-[#064E3B] hover:bg-[#047857] text-white" onClick={() => setEventDialog({ ...emptyEvent })} data-testid="calendar-new-event-btn">
                                <Plus className="h-4 w-4 mr-1" />New Event
                            </Button>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 no-print">
                    {/* Calendar Grid */}
                    <Card className="card-surface lg:col-span-2">
                        <CardHeader className="flex flex-row items-center justify-between pb-2">
                            <Button variant="ghost" size="sm" onClick={prevMonth} data-testid="calendar-prev">
                                <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <CardTitle className="font-['Outfit'] text-lg tabular-nums">
                                {MONTHS[month]} {year}
                            </CardTitle>
                            <Button variant="ghost" size="sm" onClick={nextMonth} data-testid="calendar-next">
                                <ChevronRight className="h-4 w-4" />
                            </Button>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-7 gap-px">
                                {DAYS.map(d => (
                                    <div key={d} className="text-center text-xs font-medium text-[#4B5563] py-2">{d}</div>
                                ))}
                                {cells.map((day, i) => {
                                    const events = day ? getEventsForDate(day) : [];
                                    const isSelected = day === selectedDate;
                                    const dateStr = day ? `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}` : null;
                                    const closed = day && isClosed(dateStr);
                                    return (
                                        <div
                                            key={day ? `d-${year}-${month}-${day}` : `empty-${i}`}
                                            className={`min-h-[70px] p-1 border border-[#F3F4F6] rounded-md transition-colors ${
                                                !day ? 'bg-transparent' :
                                                closed ? 'bg-[#F3F4F6] text-[#9CA3AF] cursor-default' :
                                                isSelected ? 'bg-[#064E3B]/10 border-[#064E3B] cursor-pointer' :
                                                isToday(day) ? 'bg-[#D97706]/5 border-[#D97706]/30 cursor-pointer' :
                                                'hover:bg-[#F9FAFB] cursor-pointer'
                                            }`}
                                            onClick={() => day && !closed && setSelectedDate(day === selectedDate ? null : day)}
                                            data-testid={day ? `calendar-day-${day}` : undefined}
                                        >
                                            {day && (
                                                <>
                                                    <span className={`text-xs font-medium tabular-nums ${
                                                        closed ? 'text-[#9CA3AF] line-through' :
                                                        isToday(day) ? 'text-[#D97706] font-bold' :
                                                        isSelected ? 'text-[#064E3B] font-bold' :
                                                        'text-[#4B5563]'
                                                    }`}>{day}</span>
                                                    {closed && <span className="text-[9px] text-[#9CA3AF] ml-1">closed</span>}
                                                    <div className="flex flex-wrap gap-0.5 mt-0.5">
                                                        {events.slice(0, 3).map((ev, idx) => (
                                                            <div key={`${ev.type}-${ev.label}-${idx}`} className={`h-1.5 w-1.5 rounded-full ${ev.color}`} />
                                                        ))}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Legend */}
                            <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-[#E5E7EB]">
                                <div className="flex items-center gap-1.5 text-xs text-[#4B5563]">
                                    <div className="h-2.5 w-2.5 rounded-full bg-[#064E3B]" /> Classes
                                </div>
                                <div className="flex items-center gap-1.5 text-xs text-[#4B5563]">
                                    <div className="h-2.5 w-2.5 rounded-full bg-[#D97706]" /> Homework Due
                                </div>
                                <div className="flex items-center gap-1.5 text-xs text-[#4B5563]">
                                    <div className="h-2.5 w-2.5 rounded-full bg-[#7C3AED]" /> Activities
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Side Panel */}
                    <div className="space-y-4">
                        {/* Selected Day Detail */}
                        <Card className="card-surface">
                            <CardHeader className="pb-2">
                                <CardTitle className="font-['Outfit'] text-sm">
                                    {selectedDate
                                        ? `${MONTHS[month]} ${selectedDate}, ${year}`
                                        : 'Select a date'}
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {selectedDate ? (
                                    selectedEvents.length > 0 ? (
                                        <div className="space-y-3">
                                            {selectedEvents.map((ev, idx) => (
                                                <div key={`${ev.type}-${ev.label}-${idx}`} className="flex items-start gap-3 p-2 rounded-lg bg-[#FAF9F6]">
                                                    <div className={`h-8 w-8 rounded-lg ${ev.color} flex items-center justify-center text-white flex-shrink-0`}>
                                                        {ev.type === 'class' && <BookOpen className="h-4 w-4" />}
                                                        {ev.type === 'homework' && <GraduationCap className="h-4 w-4" />}
                                                        {ev.type === 'activity' && <Activity className="h-4 w-4" />}
                                                        {ev.type === 'admin' && <Info className="h-4 w-4" />}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium text-[#1F2937]">{ev.label}</p>
                                                        {ev.time && (
                                                            <p className="text-xs text-[#4B5563] flex items-center mt-0.5">
                                                                <Clock className="h-3 w-3 mr-1" /> {ev.time}
                                                            </p>
                                                        )}
                                                        <Badge variant="outline" className="mt-1 text-xs capitalize">{ev.type === 'admin' ? 'event' : ev.type}</Badge>
                                                    </div>
                                                    {isAdmin && ev.type === 'admin' && ev._raw && (
                                                        <div className="flex flex-col gap-1">
                                                            <Button size="sm" variant="ghost" onClick={() => setEventDialog({ ...ev._raw })} data-testid={`edit-event-${ev.id}`}><Edit className="h-3.5 w-3.5" /></Button>
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                className="text-[#EF4444]"
                                                                onClick={async () => {
                                                                    if (!window.confirm('Delete this event?')) return;
                                                                    try {
                                                                        await axios.delete(`${API_URL}/api/calendar/events/${ev.id}`, { withCredentials: true });
                                                                        toast.success('Event deleted');
                                                                        fetchData();
                                                                    } catch { toast.error('Failed to delete'); }
                                                                }}
                                                                data-testid={`delete-event-${ev.id}`}
                                                            ><Trash2 className="h-3.5 w-3.5" /></Button>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-sm text-[#4B5563] py-4 text-center">No events for this date</p>
                                    )
                                ) : (
                                    <p className="text-sm text-[#4B5563] py-4 text-center">Click a date to see details</p>
                                )}
                            </CardContent>
                        </Card>

                        {/* Program Schedule */}
                        <Card className="card-surface">
                            <CardHeader className="pb-2">
                                <CardTitle className="font-['Outfit'] text-sm">Program Schedule</CardTitle>
                            </CardHeader>
                            <CardContent>
                                {programs.length > 0 ? (
                                    <div className="space-y-3">
                                        {programs.map(p => (
                                            <div key={p.id} className="p-3 bg-[#FAF9F6] rounded-lg">
                                                <p className="font-medium text-sm text-[#1F2937]">{p.name}</p>
                                                <div className="flex flex-wrap gap-1 mt-1">
                                                    {p.days?.map(d => (
                                                        <Badge key={d} variant="outline" className="text-xs">{d}</Badge>
                                                    ))}
                                                </div>
                                                <p className="text-xs text-[#4B5563] mt-1 flex items-center tabular-nums">
                                                    <Clock className="h-3 w-3 mr-1" /> {p.start_time} - {p.end_time}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-sm text-[#4B5563] py-2 text-center">No programs found</p>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </div>

                {/* Annual print view — hidden on screen, revealed on print */}
                <div className="print-annual hidden">
                    <div className="print-annual-header">
                        <h1>NurulHuda Quranic School — Annual Calendar {year}</h1>
                    </div>
                    <div className="print-annual-grid">
                        {Array.from({ length: 12 }).map((_, mi) => {
                            const mDate = new Date(year, mi, 1);
                            const first = mDate.getDay();
                            const mOffset = (first + 6) % 7;
                            const dim = new Date(year, mi + 1, 0).getDate();
                            const monthCells = [];
                            for (let i = 0; i < mOffset; i++) monthCells.push(null);
                            for (let d = 1; d <= dim; d++) monthCells.push(d);
                            const monthName = mDate.toLocaleString('en-US', { month: 'long' });
                            return (
                                <div key={mi} className="print-month">
                                    <div className="print-month-title">{monthName}</div>
                                    <div className="print-days-header">
                                        {DAYS.map(d => <span key={d}>{d[0]}</span>)}
                                    </div>
                                    <div className="print-days-grid">
                                        {monthCells.map((day, i) => {
                                            const dateStr = day ? `${year}-${String(mi + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}` : null;
                                            const closed = day && closedSet.has(dateStr);
                                            return (
                                                <span key={i} className={`print-day ${closed ? 'closed' : ''}`}>{day || ''}</span>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <div className="print-legend">Closed days are shaded. School-wide holidays only.</div>
                </div>
            </div>

            {/* Admin event editor */}
            {isAdmin && (
                <Dialog open={!!eventDialog} onOpenChange={(o) => !o && setEventDialog(null)}>
                    <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                        <DialogHeader><DialogTitle className="font-['Outfit']">{eventDialog?.id ? 'Edit event' : 'New event'}</DialogTitle></DialogHeader>
                        {eventDialog && (
                            <div className="space-y-3">
                                <div className="space-y-1"><Label>Title *</Label><Input value={eventDialog.title || ''} onChange={(e) => setEventDialog({ ...eventDialog, title: e.target.value })} data-testid="event-title" /></div>
                                <div className="space-y-1"><Label>Description</Label><Textarea rows={3} value={eventDialog.description || ''} onChange={(e) => setEventDialog({ ...eventDialog, description: e.target.value })} /></div>
                                <div className="grid grid-cols-2 gap-2">
                                    <div className="space-y-1"><Label>Start *</Label><Input type={eventDialog.all_day ? 'date' : 'datetime-local'} value={eventDialog.start || ''} onChange={(e) => setEventDialog({ ...eventDialog, start: e.target.value })} data-testid="event-start" /></div>
                                    <div className="space-y-1"><Label>End</Label><Input type={eventDialog.all_day ? 'date' : 'datetime-local'} value={eventDialog.end || ''} onChange={(e) => setEventDialog({ ...eventDialog, end: e.target.value })} /></div>
                                </div>
                                <label className="flex items-center gap-2 text-sm">
                                    <Checkbox checked={!!eventDialog.all_day} onCheckedChange={(v) => setEventDialog({ ...eventDialog, all_day: !!v })} />
                                    All-day
                                </label>
                                <div className="space-y-1">
                                    <Label>Audience — roles</Label>
                                    <div className="flex gap-3">
                                        {['parent','teacher'].map(r => (
                                            <label key={r} className="flex items-center gap-2 text-sm capitalize">
                                                <Checkbox
                                                    checked={eventDialog.target_roles?.includes(r)}
                                                    onCheckedChange={(v) => {
                                                        const set = new Set(eventDialog.target_roles || []);
                                                        if (v) set.add(r); else set.delete(r);
                                                        setEventDialog({ ...eventDialog, target_roles: Array.from(set) });
                                                    }}
                                                    data-testid={`event-role-${r}`}
                                                /> {r}s
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                {/* Branch / Program / Class multi-select */}
                                <MultiPicker
                                    label="Branches (leave empty for all)"
                                    options={Array.from(new Set(programs.map(p => p.location).filter(Boolean))).map(b => ({ value: b, label: b }))}
                                    values={eventDialog.branches || []}
                                    onChange={(vals) => setEventDialog({ ...eventDialog, branches: vals })}
                                    testid="event-branches"
                                />
                                <MultiPicker
                                    label="Programs (leave empty for all)"
                                    options={programs.map(p => ({ value: p.id, label: p.name }))}
                                    values={eventDialog.program_ids || []}
                                    onChange={(vals) => setEventDialog({ ...eventDialog, program_ids: vals })}
                                    testid="event-programs"
                                />
                                <MultiPicker
                                    label="Classes (leave empty for all)"
                                    options={classes.map(c => ({ value: c.id, label: c.name || c.arabic_name || c.english_name || c.id }))}
                                    values={eventDialog.class_ids || []}
                                    onChange={(vals) => setEventDialog({ ...eventDialog, class_ids: vals })}
                                    testid="event-classes"
                                />
                            </div>
                        )}
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setEventDialog(null)}>Cancel</Button>
                            <Button
                                className="bg-[#064E3B] hover:bg-[#047857] text-white"
                                disabled={saving || !eventDialog?.title || !eventDialog?.start}
                                onClick={async () => {
                                    setSaving(true);
                                    try {
                                        const payload = {
                                            title: eventDialog.title,
                                            description: eventDialog.description || '',
                                            start: eventDialog.start,
                                            end: eventDialog.end || eventDialog.start,
                                            all_day: !!eventDialog.all_day,
                                            color: eventDialog.color || '#064E3B',
                                            target_roles: eventDialog.target_roles || [],
                                            branches: eventDialog.branches || [],
                                            program_ids: eventDialog.program_ids || [],
                                            class_ids: eventDialog.class_ids || [],
                                        };
                                        if (eventDialog.id) {
                                            await axios.put(`${API_URL}/api/calendar/events/${eventDialog.id}`, payload, { withCredentials: true });
                                            toast.success('Event updated');
                                        } else {
                                            await axios.post(`${API_URL}/api/calendar/events`, payload, { withCredentials: true });
                                            toast.success('Event created');
                                        }
                                        setEventDialog(null);
                                        fetchData();
                                    } catch (err) {
                                        toast.error(err.response?.data?.detail || 'Failed to save');
                                    } finally { setSaving(false); }
                                }}
                                data-testid="event-save-btn"
                            >{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (eventDialog?.id ? 'Update' : 'Create')}</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
        </DashboardLayout>
    );
};

const MultiPicker = ({ label, options, values, onChange, testid }) => {
    const toggle = (v) => {
        const set = new Set(values || []);
        if (set.has(v)) set.delete(v); else set.add(v);
        onChange(Array.from(set));
    };
    return (
        <div className="space-y-1">
            <Label>{label}</Label>
            <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-2 border border-[#E5E7EB] rounded-md bg-white" data-testid={testid}>
                {options.length === 0 && <span className="text-xs text-[#6B7280]">No options</span>}
                {options.map(o => (
                    <label key={o.value} className={`text-xs px-2 py-1 border rounded cursor-pointer ${values?.includes(o.value) ? 'bg-[#064E3B] text-white border-[#064E3B]' : 'bg-white border-[#E5E7EB] text-[#374151]'}`}>
                        <input type="checkbox" className="hidden" checked={values?.includes(o.value)} onChange={() => toggle(o.value)} />
                        {o.label}
                    </label>
                ))}
            </div>
        </div>
    );
};

export default SchoolCalendar;
